import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Check, Copy, ArrowRight, ArrowLeft, AlertTriangle, Lock, Sparkles, Receipt, UserPlus, KanbanSquare, Users, ListChecks, Save, FileText, CheckCircle2, Circle, ShieldCheck, Building2, Globe, Contact, MapPin, Palette, Crown, Search, Headphones, LayoutDashboard, RefreshCw, BarChart3, Layers, Inbox, Repeat, Coins } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { PageHeader, Card, Spinner, chf } from "@/components/common";
import LogoUpload from "@/components/LogoUpload";
import { useAuth } from "@/context/AuthContext";
import { canCreateClient } from "../../lib/clientPermissions";

const BRANCHEN = ["Brocki / Secondhand", "Handwerk", "Beauty & Wellness", "Gastro & Hotels", "Dienstleister", "Gesundheit", "Vereine", "Andere"];
const LANGS = [["de", "Deutsch"], ["fr", "Français"], ["it", "Italiano"], ["en", "English"]];
const STATI = [["in_vorbereitung", "In Vorbereitung"], ["aktiv", "Aktiv"], ["wartung", "Wartung"], ["pausiert", "Pausiert"], ["archiviert", "Archiviert"], ["gekündigt", "Gekündigt"]];

const TIER_BY_NAME = { Start: "start", Business: "business", Wachstum: "wachstum", Individuell: "individuell" };
const INCLUDED = {
  start: ["cockpit", "website", "tasks", "support", "report"],
  business: ["cockpit", "website", "tasks", "support", "report", "leads", "invoices", "next_steps", "performance"],
  wachstum: ["cockpit", "website", "tasks", "support", "report", "leads", "invoices", "next_steps", "performance"],
  individuell: [],
};
const TIER_BENEFITS = {
  wachstum: ["Höhere Priorität im Support", "Wachstumsanalyse", "Erweiterter Monatsreport", "Etiketten & Aufgaben- & Ticket-Automatisierung"],
};
const REC_KEYS = ["socialcheck", "signaturemanager", "content"];
const isMonthly = (m) => (m.billing || (m.price_chf ? "monthly" : "included")) === "monthly";
const isOneTime = (m) => m.billing === "one_time";
const isOffer = (m) => m.billing === "offer" || m.on_request;
const moduleGroup = (m) => (m.category === "basis" ? "BASIS" : !isOffer(m) ? "A" : REC_KEYS.includes(m.key) ? "B" : "C");

const CMP_ORDER = ["Start", "Business", "Wachstum", "Individuell"];
const CMP_GROUPS = [
  { title: "Website", icon: Globe, rows: [
    ["Seitenumfang", ["Startseite / Onepager", "Bis 5 Seiten", "Bis 8 Seiten", "Nach Projekt"]],
    ["Mobil optimiert", ["Ja", "Ja", "Ja", "Ja"]],
    ["Kontaktformular", ["Ja", "Ja", "Ja", "Ja"]],
    ["Leistungsseiten", ["–", "Ja", "Ja", "Ja"]],
    ["Referenzbereich", ["–", "Ja", "Ja", "Ja"]],
    ["Blog / News", ["–", "–", "Ja", "Ja"]],
  ] },
  { title: "Sichtbarkeit", icon: Search, rows: [
    ["SEO-Basis", ["Ja", "Ja", "Ja", "Ja"]],
    ["SEO-Struktur", ["–", "Basis", "Ja", "Ja"]],
    ["Conversion-Optimierung", ["–", "Basis", "Ja", "Ja"]],
    ["Google Business Unterstützung", ["–", "–", "Ja", "Ja"]],
  ] },
  { title: "Betreuung", icon: Headphones, rows: [
    ["Technische Betreuung", ["Ja", "Ja", "Ja", "Ja"]],
    ["Kleine Anpassungen", ["–", "Ja", "Ja", "Ja"]],
    ["Laufende Betreuung", ["–", "Ja", "Ja", "Ja"]],
    ["Priorisierter Support", ["–", "–", "Ja", "Ja"]],
  ] },
  { title: "Cockpit & Support", icon: LayoutDashboard, rows: [
    ["Kunden-Cockpit", ["Basic", "Standard", "Erweitert", "Individuell"]],
    ["Support-Ticket-System", ["Ja", "Ja", "Ja", "Ja"]],
    ["Nächste Schritte / Aufgaben", ["Ja", "Ja", "Ja", "Ja"]],
    ["Monats-Check / Weiterentwicklung", ["–", "Basis", "Ja", "Ja"]],
    ["Besuche & Performance", ["–", "Ja", "Ja", "Ja"]],
    ["Rechnungen im Cockpit", ["Ja", "Ja", "Ja", "Ja"]],
  ] },
];

const KEWAG_EXTRAS = [
  { icon: Users, title: "Persönliche Betreuung statt Baukasten", desc: "Echte Ansprechpartner statt anonymer Website-Baukasten." },
  { icon: LayoutDashboard, title: "Kunden-Cockpit mit Aufgaben, Support und Status", desc: "Alles zur Website gebündelt an einem Ort." },
  { icon: RefreshCw, title: "Laufende Weiterentwicklung möglich", desc: "Die Website wächst Schritt für Schritt mit." },
  { icon: BarChart3, title: "Zusatzmodule für SEO, Leads und Wachstum", desc: "Gezielt erweitern, wenn es sich lohnt." },
  { icon: Layers, title: "Klare Struktur für Schweizer KMU", desc: "Aufgeräumt, verständlich und auf den Punkt." },
  { icon: Inbox, title: "Alles an einem Ort statt Chaos per E-Mail", desc: "Anfragen, Aufgaben und Rechnungen zentral gebündelt." },
];

const inputCls = "w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-[15px] text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-[#A3E635]/50 focus:border-[#A3E635] transition-all";
const DOMAIN_RE = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i;

const Field = ({ label, hint, children }) => (
  <div>
    <label className="text-[12px] font-bold uppercase tracking-[0.14em] text-gray-400">{label}</label>
    <div className="mt-2">{children}</div>
    {hint && <p className="text-[12px] text-gray-500 mt-1.5">{hint}</p>}
  </div>
);
const SubHead = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-[#A3E635] pt-1 pb-1 border-b border-white/5 mb-1">
    <Icon className="w-4 h-4" /> {children}
  </div>
);
const CardHead = ({ children, hint, icon: Icon }) => (
  <div className="mb-5">
    <h3 className="text-[19px] leading-7 font-bold text-white tracking-tight flex items-center gap-2.5">{Icon && <Icon className="w-5 h-5 text-[#A3E635]" />}{children}</h3>
    {hint && <p className="text-[13px] text-gray-500 mt-1.5">{hint}</p>}
  </div>
);

export default function ClientCreate() {
  const { user } = useAuth();
  const { t } = useI18n();
  if (!canCreateClient(user)) {
    return (
      <Card className="p-6 max-w-xl" data-testid="client-create-denied">
        <h2 className="font-bold flex items-center gap-2"><Lock className="w-4 h-4 text-gray-400" /> {t("Kein Anlegerecht")}</h2>
        <p className="text-sm text-gray-400 mt-2">{t("Ihre Rolle oder Ihr Kundenscope erlaubt keine Kundenanlage. Bitte wenden Sie sich an einen Super Admin.")}</p>
      </Card>
    );
  }
  return <ClientCreateForm />;
}

function ClientCreateForm() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [fromLead, setFromLead] = useState(null);
  const [packages, setPackages] = useState([]);
  const [modules, setModules] = useState([]);
  const [recByBranche, setRecByBranche] = useState({});
  const [existingDomains, setExistingDomains] = useState([]);
  const [step, setStep] = useState("form");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [manual, setManual] = useState(false);
  const [draftId, setDraftId] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [restore, setRestore] = useState(null);
  const [f, setF] = useState({
    company_name: "", branche: BRANCHEN[0], domain: "", app_domain: "", package_id: "", contract_months: 24,
    client_type: "paying",
    logo_url: "", primary_color: "#A3E635", contact_person: "", email: "", phone: "",
    internal_notes: "", language: "de", wp_url: "", status: "in_vorbereitung",
    setup_fee: 0, monthly_price: "", module_ids: [], street: "", plz: "", ort: "",
    next_action_note: "", prepare_invoice: false, price_override_reason: "", from_lead: "",
  });

  useEffect(() => {
    Promise.all([api.get(`/packages?lang=${lang}`), api.get(`/modules?lang=${lang}`), api.get("/industries"), api.get("/clients")]).then(([p, m, ind, cl]) => {
      setPackages(p.data); setModules(m.data);
      const rec = {}; ind.data.forEach((i) => { rec[i.key] = i.recommended_modules || []; });
      setRecByBranche(rec);
      setExistingDomains((cl.data || []).map((c) => (c.domain || "").trim().toLowerCase()).filter(Boolean));
      const p0 = p.data.find((x) => x.name === "Start") || p.data[0];
      setF((x) => ({ ...x, package_id: p0?.id || "", monthly_price: p0 && !p0.custom ? String(p0.price_chf) : "", setup_fee: p0?.setup_fee || 0 }));
    });
  }, []);

  useEffect(() => {
    api.get("/client-drafts").then(({ data }) => {
      if ((data || []).length && !sp.get("from_lead")) setRestore(data[0]);
    }).catch(() => {});
  }, [sp]);

  useEffect(() => {
    const lid = sp.get("from_lead");
    if (!lid) return;
    api.get(`/leads/${lid}`).then(({ data }) => {
      setFromLead(data);
      setF((x) => ({
        ...x, from_lead: lid,
        company_name: x.company_name || data.company || (data.client_id ? data.company_name : "") || "",
        contact_person: x.contact_person || data.name || "",
        email: x.email || data.email || "",
        phone: x.phone || data.phone || "",
      }));
    }).catch(() => {});
  }, [sp]);

  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const [attempted, setAttempted] = useState(false);
  const formRef = useRef(null);

  useEffect(() => {
    const id = setTimeout(() => {
      const root = formRef.current; if (!root) return;
      const map = { "cc-company": "company_name", "cc-contact": "contact_person", "cc-email": "email", "cc-domain": "domain", "cc-appdomain": "app_domain", "cc-phone": "phone", "cc-street": "street", "cc-plz": "plz", "cc-ort": "ort" };
      setF((x) => {
        const upd = {};
        Object.entries(map).forEach(([tid, key]) => {
          const el = root.querySelector(`[data-testid="${tid}"]`);
          if (el && el.value && !String(x[key] || "").trim()) upd[key] = el.value;
        });
        return Object.keys(upd).length ? { ...x, ...upd } : x;
      });
    }, 700);
    return () => clearTimeout(id);
  }, []);

  const selectedPkg = useMemo(() => packages.find((p) => p.id === f.package_id), [packages, f.package_id]);
  const tier = selectedPkg ? (TIER_BY_NAME[selectedPkg.name] || (selectedPkg.custom ? "individuell" : "start")) : null;
  const isIndividuell = !!selectedPkg?.custom;
  const effManual = manual || isIndividuell;

  const includedKeys = tier ? INCLUDED[tier] : [];
  const includedObjs = useMemo(() => (isIndividuell ? [] : modules.filter((m) => includedKeys.includes(m.key))), [modules, includedKeys, isIndividuell]);
  const addonObjs = useMemo(() => modules.filter((m) => (isIndividuell ? true : m.category !== "basis") && !includedKeys.includes(m.key)), [modules, includedKeys, isIndividuell]);
  const recKeys = recByBranche[f.branche] || [];

  const onPackage = (p) => {
    setManual(false);
    setF((x) => ({
      ...x, package_id: p.id,
      monthly_price: p.custom ? "" : String(p.price_chf),
      setup_fee: p.custom ? "" : (p.setup_fee || 0),
      contract_months: p.custom ? x.contract_months : 24,
      module_ids: [],
    }));
  };

  const toggleAddon = (id) => setF((x) => ({
    ...x, module_ids: x.module_ids.includes(id) ? x.module_ids.filter((m) => m !== id) : [...x.module_ids, id],
  }));

  // ---------- Preis-Rechner (Billing-bewusst) ----------
  const calc = useMemo(() => {
    const baseMonthly = effManual ? Number(f.monthly_price) || 0 : (selectedPkg?.custom ? 0 : selectedPkg?.price_chf || 0);
    const baseSetup = effManual ? Number(f.setup_fee) || 0 : (selectedPkg?.custom ? 0 : selectedPkg?.setup_fee || 0);
    const selAddons = addonObjs.filter((m) => f.module_ids.includes(m.id));
    const addonMonthly = selAddons.filter(isMonthly).reduce((s, m) => s + (m.price_chf || 0), 0);
    const addonOnce = selAddons.filter(isOneTime).reduce((s, m) => s + (m.price_chf || 0), 0);
    const offerSel = selAddons.filter(isOffer);
    const recPrepared = offerSel.filter((m) => moduleGroup(m) === "B");
    const individualOffers = offerSel.filter((m) => moduleGroup(m) === "C");
    const totalMonthly = baseMonthly + addonMonthly;
    const totalSetup = baseSetup + addonOnce;
    const months = Number(f.contract_months) || 0;
    const gesamt = totalSetup + totalMonthly * months;
    const paidAddons = selAddons.filter((m) => !isOffer(m));
    return { baseMonthly, baseSetup, addonMonthly, addonOnce, totalMonthly, totalSetup, months, gesamt, selAddons, paidAddons, recPrepared, individualOffers, offerSel };
  }, [effManual, f.monthly_price, f.setup_fee, f.module_ids, f.contract_months, selectedPkg, addonObjs]);

  // ---------- Validierung ----------
  const { errors, warnings } = useMemo(() => {
    const e = [], w = [];
    if (!f.company_name.trim()) e.push("Firmenname fehlt.");
    if (!f.contact_person.trim()) e.push("Ansprechpartner fehlt.");
    if (!f.email.trim()) e.push("E-Mail fehlt.");
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.push("E-Mail ist ungültig.");
    if (!f.domain.trim()) e.push("Domain fehlt.");
    else if (!DOMAIN_RE.test(f.domain.trim())) e.push("Domain ist ungültig (z. B. firma.ch).");
    else if (existingDomains.includes(f.domain.trim().toLowerCase())) e.push("Kunde mit dieser Domain existiert bereits.");
    if (f.app_domain.trim() && !DOMAIN_RE.test(f.app_domain.trim())) e.push("App-Domain ist ungültig.");
    const noteProvided = !!(f.internal_notes.trim() || f.next_action_note.trim());
    const isPaying = (f.client_type || "paying") === "paying";
    if (isPaying) {
      if (!f.package_id) e.push("Paket ist nicht gewählt.");
      if (!(Number(f.contract_months) >= 1)) e.push("Laufzeit fehlt.");
      if (isIndividuell) {
        if (!(Number(f.monthly_price) > 0)) w.push("INDIVIDUELL: Monatspreis nach Offerte definieren (oder als Offerte vorbereiten).");
        if (!(Number(f.setup_fee) > 0)) w.push("INDIVIDUELL: Setup-Preis nach Offerte definieren (oder als Offerte vorbereiten).");
        if (!noteProvided) e.push("INDIVIDUELL: Interne Notiz oder nächste Aktion ist erforderlich (Offerte vorbereiten).");
      }
      if (effManual && !isIndividuell && selectedPkg) {
        const monthlyDev = Number(f.monthly_price) !== selectedPkg.price_chf;
        const setupDev = Number(f.setup_fee) !== selectedPkg.setup_fee;
        if (monthlyDev) w.push(`Monatspreis weicht vom Paketpreis (CHF ${selectedPkg.price_chf}) ab.`);
        if (setupDev) w.push(`Setup-Preis weicht vom Paket (CHF ${selectedPkg.setup_fee}) ab.`);
        if ((monthlyDev || setupDev) && !f.price_override_reason.trim()) e.push("Grund für die manuelle Preisänderung ist erforderlich.");
      }
    }
    if (calc.offerSel.length > 0 && !noteProvided) e.push("Empfehlungs-/Offerte-Module gewählt: bitte interne Notiz oder nächste Aktion erfassen.");
    return { errors: e, warnings: w };
  }, [f, existingDomains, isIndividuell, effManual, selectedPkg, calc.offerSel.length]);

  const goTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const goReview = () => {
    if (errors.length) { setAttempted(true); toast.error(t("Bitte zuerst die Pflichtfelder ausfüllen.")); goTo("cc-sec-summary"); return; }
    setStep("review");
  };

  const validateOnly = () => {
    setAttempted(true);
    if (errors.length) { toast.error(t("Bitte zuerst die Pflichtfelder ausfüllen.")); goTo("cc-sec-summary"); }
    else toast.success(t("Alle Pflichtfelder erfüllt — bereit zum Erstellen."));
  };

  const saveDraft = async () => {
    setSavingDraft(true);
    try {
      const { data } = await api.post("/client-drafts", {
        draft_id: draftId, company_name: f.company_name || t("Unbenannter Entwurf"),
        draft_data: { f, manual },
      });
      setDraftId(data.id);
      toast.success(t("Entwurf gespeichert"));
    } catch (e) { toast.error(e.response?.data?.detail || t("Entwurf konnte nicht gespeichert werden")); }
    finally { setSavingDraft(false); }
  };

  const loadDraft = async (d) => {
    try {
      const { data } = await api.post(`/client-drafts/${d.id}/load`);
      const dd = data.draft_data || d.draft_data || {};
      if (dd.f) setF((x) => ({ ...x, ...dd.f }));
      if (typeof dd.manual === "boolean") setManual(dd.manual);
      setDraftId(d.id);
      setRestore(null);
      toast.success(t("Entwurf geladen"));
    } catch { toast.error(t("Entwurf konnte nicht geladen werden")); }
  };

  const discardDraft = async () => {
    if (!restore) return;
    try { await api.delete(`/client-drafts/${restore.id}`); } catch { /* ignore */ }
    setRestore(null);
    toast.success(t("Entwurf verworfen"));
  };

  const finalModuleIds = () => [...new Set([...includedObjs.map((m) => m.id), ...f.module_ids])];

  const submit = async () => {
    setBusy(true);
    try {
      const payload = {
        ...f, setup_fee: Number(f.setup_fee) || 0,
        monthly_price: f.monthly_price === "" ? null : Number(f.monthly_price),
        contract_months: Number(f.contract_months) || 24,
        module_ids: finalModuleIds(),
      };
      const { data } = await api.post("/clients", payload);
      toast.success(t("portalCreated"));
      if (draftId) { api.delete(`/client-drafts/${draftId}`).catch(() => {}); }
      setResult(data); setStep("done");
    } catch (err) {
      toast.error(err.response?.data?.detail || t("Fehler beim Anlegen"));
      setStep("form");
    } finally { setBusy(false); }
  };

  if (!packages.length) return <Spinner />;

  // ============ SUCCESS ============
  if (step === "done" && result) {
    const actions = [
      { label: "Kunde öffnen", icon: ArrowRight, to: `/admin/clients/${result.id}`, primary: true, tid: "succ-open" },
      { label: "Benutzer anlegen", icon: UserPlus, to: `/admin/clients/${result.id}?tab=users`, tid: "succ-users" },
      { label: "Aufgabe erstellen", icon: KanbanSquare, to: `/admin/clients/${result.id}?tab=tasks`, tid: "succ-task" },
      { label: "Rechnung vorbereiten", icon: Receipt, to: `/admin/clients/${result.id}?tab=invoices`, tid: "succ-invoice" },
      { label: "Zur Kundenliste", icon: Users, to: "/admin/clients", tid: "succ-list" },
    ];
    return (
      <div className="fade-in max-w-lg mx-auto text-center py-12" data-testid="cc-success">
        <div className="w-16 h-16 rounded-full bg-[#A3E635]/15 border border-[#A3E635]/30 flex items-center justify-center mx-auto">
          <Check className="w-8 h-8 text-[#A3E635]" />
        </div>
        <h2 className="text-3xl font-black tracking-tighter mt-6">{t("Kunde wurde erstellt.")}</h2>
        <p className="text-gray-400 mt-2">{t("Cockpit, Standardmodule, erste Aufgabe und Protokoll-Eintrag wurden automatisch angelegt.")}{result.invoice_id ? " " + t("Eine bexio-Rechnung wurde vorbereitet.") : ""}</p>
        <Card className="p-6 mt-8 text-left">
          <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-3">{t("portalAccess")}</p>
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <span className="text-gray-500 text-sm">{t("email")}</span>
            <span className="font-mono text-sm">{result.portal_email}</span>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-gray-500 text-sm">{t("password")}</span>
            <button onClick={() => { navigator.clipboard.writeText(result.portal_password); toast.success(t("Kopiert")); }}
              className="font-mono text-sm flex items-center gap-2 hover:text-[#A3E635]">
              {result.portal_password} <Copy className="w-3.5 h-3.5" />
            </button>
          </div>
        </Card>
        <div className="grid grid-cols-2 gap-3 mt-6">
          {actions.map((a) => (
            <button key={a.tid} onClick={() => nav(a.to)} data-testid={a.tid}
              className={`flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-bold text-sm transition-colors ${a.primary ? "bg-[#A3E635] hover:bg-[#BEF264] text-black col-span-2" : "bg-white/5 hover:bg-white/10 border border-white/10 text-gray-200"}`}>
              <a.icon className="w-4 h-4" /> {t(a.label)}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // ---------- Modul-Preis-Labels ----------
  const offerLabel = (m) => {
    const p = [];
    if (m.from_setup) p.push(`ab CHF ${m.from_setup}.– ${t("Setup")}`);
    if (m.from_monthly) p.push(`ab CHF ${m.from_monthly}.– ${t("/ Monat")}`);
    return p.join(" · ") || t("auf Anfrage");
  };
  const modPriceLabel = (m) => {
    if (isOneTime(m)) return `CHF ${m.price_chf}.– ${t("einmalig")}`;
    if (isOffer(m)) return offerLabel(m);
    if ((m.price_chf || 0) > 0) return `CHF ${m.price_chf}.– ${t("/ Monat")}`;
    return t("frei wählbar");
  };

  // ---------- Preis-Panel ----------
  const PricePanel = ({ hero = true }) => (
    <Card className="p-6" data-testid="cc-price-panel">
      <CardHead icon={Sparkles}>{t("Paket & Preis prüfen")}</CardHead>
      <div className="rounded-xl border border-white/10 bg-black/40 p-4 mb-4">
        <div className="text-[11px] uppercase tracking-wider text-gray-400">{t("Ausgewähltes Paket")}</div>
        <div className="text-2xl font-black tracking-tight text-white mt-0.5">{selectedPkg ? (selectedPkg.display_name || selectedPkg.name).toUpperCase() : "—"}</div>
        <div className="text-xs text-[#A3E635] mt-0.5">{t("inkl. MWST")}</div>
      </div>
      <div className="space-y-2.5 text-[15px]">
        <Row k={t("Paketpreis / Monat")} v={isIndividuell && !calc.baseMonthly ? t("nach Offerte") : chf(calc.baseMonthly)} />
        <Row k={t("Setup-Preis")} v={isIndividuell && !calc.baseSetup ? t("nach Offerte") : chf(calc.baseSetup)} />
        <Row k={t("Laufzeit")} v={`${calc.months} ${t("Monate")}`} />
        <Row k={t("Zusatzmodule / Monat")} v={chf(calc.addonMonthly)} accent={calc.addonMonthly > 0} />
        {calc.addonOnce > 0 && <Row k={t("Zusatzkosten einmalig")} v={chf(calc.addonOnce)} accent />}
        <div className="border-t border-white/10 my-1" />
        <Row k={t("Total monatlich")} v={chf(calc.totalMonthly)} bold />
        <Row k={t("Setup einmalig")} v={chf(calc.totalSetup)} bold />
      </div>
      {hero && (
        <div className="rounded-xl border border-[#A3E635]/30 bg-[#A3E635]/[0.07] p-4 text-center mt-4" data-testid="cc-hero-total">
          <div className="text-[11px] uppercase tracking-[0.16em] text-gray-300">{t("Gesamtwert {n} Monate").replace("{n}", calc.months)}</div>
          <div className="text-[40px] leading-[44px] font-black text-[#A3E635] tracking-tighter mt-1">{isIndividuell && !calc.totalMonthly ? t("nach Offerte") : chf(calc.gesamt)}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">{t("inkl. MWST")}</div>
        </div>
      )}
      <div className="mt-5 text-[12px] text-gray-500 space-y-3">
        <div>
          <div className="font-bold text-gray-400 uppercase tracking-wider mb-1.5">{t("Inklusive Module")} ({includedObjs.length})</div>
          <div className="flex flex-wrap gap-1.5">
            {includedObjs.length === 0 ? <span>{t("Freie Auswahl")}</span> : includedObjs.map((m) => (
              <span key={m.id} className="px-2 py-0.5 rounded bg-white/5 text-gray-300">{m.display_name || m.name}</span>
            ))}
          </div>
        </div>
        {calc.paidAddons.length > 0 && (
          <div>
            <div className="font-bold text-gray-400 uppercase tracking-wider mb-1.5">{t("Zusatzmodule")} ({calc.paidAddons.length})</div>
            <div className="flex flex-wrap gap-1.5">
              {calc.paidAddons.map((m) => <span key={m.id} className="px-2 py-0.5 rounded bg-[#A3E635]/10 text-[#A3E635]">{m.display_name || m.name}{m.price_chf ? ` · CHF ${m.price_chf}${isOneTime(m) ? " " + t("einmalig") : "/Mt."}` : ""}</span>)}
            </div>
          </div>
        )}
        {calc.recPrepared.length > 0 && (
          <div>
            <div className="font-bold text-gray-400 uppercase tracking-wider mb-1.5">{t("Vorbereitete Empfehlungen")} ({calc.recPrepared.length})</div>
            <div className="flex flex-wrap gap-1.5">
              {calc.recPrepared.map((m) => <span key={m.id} className="px-2 py-0.5 rounded bg-amber-400/10 text-amber-300">{m.display_name || m.name} · {t("Offerte")}</span>)}
            </div>
          </div>
        )}
        {calc.individualOffers.length > 0 && (
          <div>
            <div className="font-bold text-gray-400 uppercase tracking-wider mb-1.5">{t("Individuelle Offerten")} ({calc.individualOffers.length})</div>
            <div className="flex flex-wrap gap-1.5">
              {calc.individualOffers.map((m) => <span key={m.id} className="px-2 py-0.5 rounded bg-white/10 text-gray-200">{m.display_name || m.name} · {t("separates Angebot")}</span>)}
            </div>
          </div>
        )}
      </div>
    </Card>
  );

  // ============ REVIEW ============
  if (step === "review") {
    const D = ({ k, v }) => (<div className="flex justify-between border-b border-white/5 py-2 text-sm"><span className="text-gray-500">{k}</span><span className="font-medium text-right">{v || "—"}</span></div>);
    return (
      <div className="fade-in max-w-5xl" data-testid="cc-review">
        <PageHeader title={t("Kunde prüfen & erstellen")} subtitle={t("Bitte alle Angaben vor dem endgültigen Erstellen kontrollieren.")} />
        {warnings.length > 0 && (
          <div className="mb-5 rounded-xl border border-amber-400/30 bg-amber-400/10 p-4" data-testid="cc-review-warnings">
            {warnings.map((wn, i) => <div key={i} className="text-sm text-amber-200 flex items-center gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> {t(wn)}</div>)}
          </div>
        )}
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="p-6 lg:col-span-2">
            <h3 className="font-bold mb-3">{t("Kundendaten")}</h3>
            <div className="grid sm:grid-cols-2 gap-x-6">
              <D k={t("Firmenname")} v={f.company_name} />
              <D k={t("Branche")} v={f.branche} />
              <D k={t("Ansprechpartner")} v={f.contact_person} />
              <D k={t("E-Mail")} v={f.email} />
              <D k={t("Telefon")} v={f.phone} />
              <D k={t("Adresse")} v={[f.street, [f.plz, f.ort].filter(Boolean).join(" ")].filter(Boolean).join(", ")} />
              <D k={t("Domain")} v={f.domain} />
              <D k={t("App-Domain")} v={f.app_domain} />
              <D k={t("Status")} v={STATI.find(([c]) => c === f.status)?.[1]} />
              <D k={t("Nächste Aktion")} v={f.next_action_note} />
            </div>
            <h3 className="font-bold mt-5 mb-2">{t("Module")}</h3>
            <div className="flex flex-wrap gap-1.5">
              {includedObjs.map((m) => <span key={m.id} className="text-xs px-2 py-1 rounded bg-white/5 text-gray-300">{m.display_name || m.name} · {t("inkl.")}</span>)}
              {calc.paidAddons.map((m) => <span key={m.id} className="text-xs px-2 py-1 rounded bg-[#A3E635]/10 text-[#A3E635]">{m.display_name || m.name}{m.price_chf ? ` · CHF ${m.price_chf}` : ""}</span>)}
              {calc.offerSel.map((m) => <span key={m.id} className="text-xs px-2 py-1 rounded bg-amber-400/10 text-amber-300">{m.display_name || m.name} · {t("Offerte")}</span>)}
            </div>
            {f.prepare_invoice && <div className="mt-4 text-sm text-[#A3E635] flex items-center gap-2"><Receipt className="w-4 h-4" /> {t("bexio-Rechnung wird vorbereitet (Setup + 1. Monat).")}</div>}
            {f.price_override_reason && !isIndividuell && <div className="mt-3 text-sm text-amber-300 flex items-center gap-2"><AlertTriangle className="w-4 h-4 shrink-0" /> {t("Manuelle Preisänderung:")} {f.price_override_reason}</div>}
            {f.internal_notes && <div className="mt-3 text-sm text-gray-400"><span className="text-gray-500">{t("notes")}: </span>{f.internal_notes}</div>}
          </Card>
          <div><PricePanel /></div>
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={() => setStep("form")} data-testid="cc-back" className="bg-white/5 hover:bg-white/10 border border-white/10 py-3 px-6 rounded-lg flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> {t("Zurück bearbeiten")}
          </button>
          <button onClick={submit} disabled={busy} data-testid="cc-confirm"
            className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-3 px-8 rounded-lg transition-colors disabled:opacity-60 flex items-center gap-2">
            <Check className="w-4 h-4" /> {busy ? t("Erstellt…") : t("Kunde endgültig erstellen")}
          </button>
        </div>
      </div>
    );
  }

  // ---------- Prüfliste & Stepper ----------
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((f.email || "").trim());
  const domainTrim = (f.domain || "").trim().toLowerCase();
  const domainFormatOk = DOMAIN_RE.test(domainTrim);
  const domainFree = !!domainTrim && !existingDomains.includes(domainTrim);
  const appDomainOk = !(f.app_domain || "").trim() || DOMAIN_RE.test(f.app_domain.trim());
  const isPaying = (f.client_type || "paying") === "paying";
  const noteProvided = !!(f.internal_notes.trim() || f.next_action_note.trim());
  const noteRequired = (isIndividuell || calc.offerSel.length > 0);
  const checks = [
    { label: t("Firmenname / Projektname"), ok: !!f.company_name.trim() },
    { label: t("Branche"), ok: !!f.branche },
    { label: t("Kontaktperson"), ok: !!f.contact_person.trim() },
    { label: t("E-Mail gültig"), ok: emailValid },
    { label: `${t("Domain")}${domainTrim ? ` (${domainTrim})` : ""}`, ok: domainFormatOk },
    { label: t("App-Domain (optional)"), ok: appDomainOk },
    { label: t("Paket & Preis geprüft"), ok: !isPaying || (!!f.package_id && (isIndividuell ? true : Number(f.monthly_price) >= 0) && Number(f.contract_months) >= 1) },
    { label: t("Domain noch frei (kein Duplikat)"), ok: domainFree },
    ...(noteRequired ? [{ label: t("Interne Notiz / nächste Aktion (Offerte)"), ok: noteProvided }] : []),
  ];
  const step1Done = !!f.company_name.trim() && !!f.contact_person.trim() && emailValid && domainFormatOk && domainFree;
  const step2Done = !isPaying || (!!f.package_id && Number(f.contract_months) >= 1 && (!isIndividuell || Number(f.monthly_price) > 0 || noteProvided));
  const stepDone = { 1: step1Done, 2: step2Done, 3: step1Done && step2Done, 4: step1Done && step2Done && errors.length === 0, 5: false };
  const activeStep = !stepDone[1] ? 1 : !stepDone[2] ? 2 : !stepDone[4] ? 4 : 5;
  const STEPS = [[1, "Kundendaten", "cc-sec-stammdaten"], [2, "Paket & Preis", "cc-sec-paket"], [3, "Module", "cc-sec-module"], [4, "Prüfung", "cc-sec-summary"], [5, "Erstellen", "cc-sec-summary"]];

  // ---------- Modul-Button ----------
  const renderMod = (m, group) => {
    const on = f.module_ids.includes(m.id);
    const rec = recKeys.includes(m.key);
    return (
      <button type="button" key={m.id} data-testid={`cc-mod-${m.key}`} onClick={() => toggleAddon(m.id)}
        className={`flex items-start gap-3 p-4 rounded-xl border text-left transition-all ${on ? "border-[#A3E635] bg-[#A3E635]/10 ring-1 ring-[#A3E635]/20" : "border-white/10 hover:border-white/25 bg-black/20"}`}>
        <div className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 mt-0.5 ${on ? "bg-[#A3E635] border-[#A3E635]" : "border-white/25"}`}>{on && <Check className="w-3.5 h-3.5 text-black" />}</div>
        <div className="min-w-0 flex-1">
          <div className="text-[14px] font-semibold leading-snug flex items-center gap-1.5 flex-wrap">{m.display_name || m.name}
            {rec && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#A3E635]/15 text-[#A3E635] font-bold uppercase tracking-wide">{t("empfohlen")}</span>}
            {group === "C" && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/10 text-gray-300 font-bold uppercase tracking-wide">{t("separates Angebot")}</span>}
          </div>
          <div className="text-[12px] text-gray-500 mt-1">{modPriceLabel(m)}</div>
        </div>
      </button>
    );
  };

  const groupA = addonObjs.filter((m) => moduleGroup(m) === "A");
  const groupAMonthly = groupA.filter(isMonthly);
  const groupAOnce = groupA.filter(isOneTime);
  const groupBASIS = addonObjs.filter((m) => moduleGroup(m) === "BASIS");
  const groupB = addonObjs.filter((m) => moduleGroup(m) === "B");
  const groupC = addonObjs.filter((m) => moduleGroup(m) === "C");
  const cmpPkgs = CMP_ORDER.map((n) => packages.find((p) => p.name === n)).filter(Boolean);
  const recIdx = cmpPkgs.findIndex((p) => p.is_recommended);

  // ============ FORM ============
  return (
    <div className="fade-in" ref={formRef} data-testid="cc-root">
      <PageHeader title={t("newClient")} subtitle={t("Sicherer Kunden-Setup-Assistent — Paket, Module und Preis live prüfen.")} />

      {/* Fortschrittsanzeige */}
      <div className="mb-8 flex items-center gap-1 overflow-x-auto pb-4 border-b border-white/10" data-testid="cc-stepper">
        {STEPS.map(([n, label, target], i) => {
          const done = stepDone[n]; const active = activeStep === n;
          return (
            <React.Fragment key={n}>
              <button type="button" onClick={() => goTo(target)} data-testid={`cc-step-${n}`}
                className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-lg text-[14px] whitespace-nowrap transition-colors ${active ? "bg-[#A3E635]/10 text-white" : done ? "text-[#A3E635] hover:bg-white/5" : "text-gray-500 hover:text-gray-300"}`}>
                <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[12px] font-bold ${done ? "bg-[#A3E635] text-black" : active ? "bg-[#A3E635]/20 text-[#A3E635] ring-1 ring-[#A3E635]" : "bg-white/10 text-gray-400"}`}>
                  {done ? <Check className="w-3.5 h-3.5" /> : n}
                </span>
                <span className="font-semibold">{t(label)}</span>
              </button>
              {i < STEPS.length - 1 && <span className="w-6 h-px bg-white/10 shrink-0" />}
            </React.Fragment>
          );
        })}
      </div>

      {fromLead && (
        <div className="mb-5 rounded-xl border border-[#A3E635]/30 bg-[#A3E635]/[0.06] p-4 text-[14px] text-[#A3E635] flex items-center gap-2" data-testid="cc-fromlead-banner">
          <UserPlus className="w-4 h-4 shrink-0" /> {t("Kunde wird aus Lead „{name}\" erstellt — Daten wurden übernommen.").replace("{name}", fromLead.company_name)}
        </div>
      )}

      {restore && (
        <div className="mb-6 rounded-xl border border-white/10 bg-white/5 p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="cc-restore-banner">
          <div className="text-[14px] text-gray-300 flex items-center gap-2 flex-wrap">
            <FileText className="w-5 h-5 text-[#A3E635] shrink-0" /> {t("Gespeicherter Entwurf gefunden")}:
            <span className="font-semibold text-white">{restore.company_name || t("Unbenannt")}</span>
            <span className="text-gray-500 text-[12px]">· {restore.updated_at ? new Date(restore.updated_at).toLocaleString("de-CH", { dateStyle: "short", timeStyle: "short" }) : ""}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => loadDraft(restore)} data-testid="cc-restore-load" className="bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-2 px-4 rounded-lg text-[13px]">{t("Entwurf laden")}</button>
            <button onClick={discardDraft} data-testid="cc-restore-dismiss" className="bg-white/5 border border-white/10 hover:bg-white/10 text-gray-300 py-2 px-4 rounded-lg text-[13px]">{t("Verwerfen")}</button>
          </div>
        </div>
      )}

      {/* === Premium 3-Spalten-Workspace (28% / 47% / 25%) === */}
      <div className="grid grid-cols-1 xl:grid-cols-[26fr_50fr_24fr] gap-6 2xl:gap-8 items-start">

        {/* ===== LINKS: Kundendaten ===== */}
        <div id="cc-sec-stammdaten" className="space-y-6">
          <Card className="p-6 space-y-6">
            <SubHead icon={Building2}>{t("Kundendaten")}</SubHead>
            <Field label={t("Firmenname / Projektname")}>
              <input data-testid="cc-company" className={inputCls} value={f.company_name} onChange={(e) => set("company_name", e.target.value)} placeholder="Fundio" />
            </Field>
            <Field label={t("branche")}>
              <select data-testid="cc-branche" className={inputCls} value={f.branche} onChange={(e) => set("branche", e.target.value)}>
                {BRANCHEN.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("Status")}>
                <select data-testid="cc-status" className={inputCls} value={f.status} onChange={(e) => set("status", e.target.value)}>
                  {STATI.map(([c, l]) => <option key={c} value={c}>{t(l)}</option>)}
                </select>
              </Field>
              <Field label={t("Typ")}>
                <select data-testid="cc-client-type" className={inputCls} value={f.client_type} onChange={(e) => set("client_type", e.target.value)}>
                  <option value="paying">{t("Zahlender Kunde")}</option>
                  <option value="internal">{t("Internes KEWAG-System (CHF 0)")}</option>
                  <option value="test">{t("Testsystem (nur QA)")}</option>
                </select>
              </Field>
            </div>

            <SubHead icon={Globe}>{t("Web & Domains")}</SubHead>
            <Field label={t("domain")}>
              <input data-testid="cc-domain" className={inputCls} value={f.domain} onChange={(e) => set("domain", e.target.value)} placeholder="fundio.ch" />
            </Field>
            <Field label={t("App-Domain (optional)")}>
              <input data-testid="cc-appdomain" className={inputCls} value={f.app_domain} onChange={(e) => set("app_domain", e.target.value)} placeholder="app.fundio.ch" />
            </Field>

            <SubHead icon={Contact}>{t("Kontakt")}</SubHead>
            <Field label={t("Kontaktperson")}>
              <input data-testid="cc-contact" className={inputCls} value={f.contact_person} onChange={(e) => set("contact_person", e.target.value)} placeholder="Martin Kläy" />
            </Field>
            <Field label={t("email")}>
              <input data-testid="cc-email" type="email" className={inputCls} value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="info@fundio.ch" />
            </Field>
            <Field label={t("phone")}>
              <input data-testid="cc-phone" className={inputCls} value={f.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+41 ..." />
            </Field>

            <SubHead icon={MapPin}>{t("Adresse (optional)")}</SubHead>
            <Field label={t("Strasse")}>
              <input data-testid="cc-street" className={inputCls} value={f.street} onChange={(e) => set("street", e.target.value)} placeholder="Musterstrasse 1" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label={t("PLZ")}><input data-testid="cc-plz" className={inputCls} value={f.plz} onChange={(e) => set("plz", e.target.value)} placeholder="3000" /></Field>
              <Field label={t("Ort")}><input data-testid="cc-ort" className={inputCls} value={f.ort} onChange={(e) => set("ort", e.target.value)} placeholder="Bern" /></Field>
            </div>

            <SubHead icon={Palette}>{t("Darstellung & Sprache")}</SubHead>
            <Field label={t("language")}>
              <select className={inputCls} value={f.language} onChange={(e) => set("language", e.target.value)}>
                {LANGS.map(([c, l]) => <option key={c} value={c}>{t(l)}</option>)}
              </select>
            </Field>
            <Field label={t("color")}>
              <div className="flex items-center gap-3">
                <input type="color" value={f.primary_color} onChange={(e) => set("primary_color", e.target.value)} className="w-12 h-12 rounded-lg bg-black border border-white/10 cursor-pointer shrink-0" />
                <input className={inputCls} value={f.primary_color} onChange={(e) => set("primary_color", e.target.value)} />
              </div>
            </Field>
            <Field label={t("logo") + " (optional)"}>
              <LogoUpload value={f.logo_url} onChange={(v) => set("logo_url", v)} color={f.primary_color} />
            </Field>

            <SubHead icon={ArrowRight}>{t("Nächste Aktion (optional)")}</SubHead>
            <input data-testid="cc-nextaction" className={inputCls} value={f.next_action_note} onChange={(e) => set("next_action_note", e.target.value)} placeholder="z. B. Erstgespräch vereinbaren" />
          </Card>
        </div>

        {/* ===== MITTE: Paket, Module, Vergleich ===== */}
        <div className="space-y-8">
          {/* Paket */}
          <Card id="cc-sec-paket" className="p-7">
            <CardHead hint={t("Preise inkl. MWST · Laufzeit 24 Monate. WACHSTUM ist unsere Empfehlung.")}>{t("Paket wählen")}</CardHead>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
              {packages.map((p) => {
                const sel = f.package_id === p.id;
                const rec = !!p.is_recommended;
                const cls = sel
                  ? "border-2 border-[#A3E635] bg-[#A3E635]/10 ring-1 ring-[#A3E635]/30 shadow-[0_0_28px_rgba(163,230,53,0.12)]"
                  : rec
                    ? "border-2 border-[#A3E635]/50 bg-[#A3E635]/[0.04] hover:border-[#A3E635]/70"
                    : "border border-white/10 hover:border-white/25 bg-black/20";
                return (
                  <button type="button" key={p.id} data-testid={`cc-pkg-${p.name}`} onClick={() => onPackage(p)}
                    className={`relative text-left p-5 pt-6 rounded-2xl transition-all duration-200 ${cls}`}>
                    {rec && (
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[10px] font-black px-3 py-1 rounded-full bg-[#A3E635] text-black uppercase tracking-wider shadow-[0_4px_14px_rgba(163,230,53,0.4)]" data-testid={`cc-pkg-recommended-${p.name}`}>
                        <Crown className="w-3 h-3" /> {t("Empfohlen")}
                      </span>
                    )}
                    <div className="flex items-center justify-between gap-2">
                      <div className={`font-black text-[15px] uppercase tracking-wide ${rec ? "text-[#A3E635]" : "text-white"}`}>{p.display_name || p.name}</div>
                      <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 ${sel ? "border-[#A3E635] bg-[#A3E635]/10" : "border-white/25"}`}>
                        {sel && <span className="w-2.5 h-2.5 rounded-full bg-[#A3E635]" />}
                      </span>
                    </div>
                    {p.custom ? (
                      <>
                        <div className="text-[22px] leading-7 font-black tracking-tight mt-3 text-white">{t("Auf Anfrage")}</div>
                        <div className="inline-flex items-center gap-1.5 text-[12px] text-[#A3E635] mt-2 font-bold"><Receipt className="w-3.5 h-3.5" /> {t("Offerte vorbereiten")}</div>
                        <div className="border-t border-white/10 mt-3 pt-3 text-[12px] text-gray-400 leading-snug">{p.description}</div>
                      </>
                    ) : (
                      <>
                        <div className="text-[32px] leading-9 font-black tracking-tighter mt-3 text-white">CHF {p.price_chf}.–<span className="text-[12px] text-gray-500 font-normal"> / Mt.</span></div>
                        <div className="mt-3 space-y-1.5 border-t border-white/10 pt-3">
                          <div className="flex items-center justify-between text-[12px]"><span className="text-gray-500">{t("Setup")}</span><span className="font-semibold text-gray-200">CHF {p.setup_fee}.–</span></div>
                          <div className="flex items-center justify-between text-[12px]"><span className="text-gray-500">{t("Laufzeit")}</span><span className="font-semibold text-gray-200">{p.default_duration_months || 24} {t("Monate")}</span></div>
                        </div>
                        <div className="text-[12px] text-gray-400 mt-3 leading-snug">{p.description}</div>
                      </>
                    )}
                  </button>
                );
              })}
            </div>
            <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
              <label className="flex items-center gap-2.5 text-[14px] cursor-pointer">
                <input type="checkbox" data-testid="cc-manual-toggle" checked={effManual} disabled={isIndividuell} onChange={(e) => setManual(e.target.checked)} className="w-5 h-5 accent-[#A3E635]" />
                <span className={isIndividuell ? "text-gray-500" : "text-gray-300"}>{t("Preis manuell anpassen")}{isIndividuell ? " (bei INDIVIDUELL immer aktiv)" : ""}</span>
              </label>
            </div>
            <div className="grid sm:grid-cols-3 gap-5 mt-5">
              <Field label={t("Monatspreis (CHF)")}>
                <input type="number" min="0" data-testid="cc-monthly" disabled={!effManual} className={`${inputCls} ${!effManual ? "opacity-60 cursor-not-allowed" : ""}`}
                  value={f.monthly_price} onChange={(e) => set("monthly_price", e.target.value)} placeholder={t("aus Paket")} />
              </Field>
              <Field label={t("Setup-Gebühr (CHF)")}>
                <input type="number" min="0" data-testid="cc-setup" disabled={!effManual} className={`${inputCls} ${!effManual ? "opacity-60 cursor-not-allowed" : ""}`}
                  value={f.setup_fee} onChange={(e) => set("setup_fee", e.target.value)} placeholder={t("aus Paket")} />
              </Field>
              <Field label={t("Laufzeit (Monate)")}>
                <input type="number" min="1" data-testid="cc-contract" className={inputCls} value={f.contract_months} onChange={(e) => set("contract_months", parseInt(e.target.value) || "")} />
              </Field>
            </div>
            {manual && !isIndividuell && (
              <div className="mt-5">
                <Field label={t("Grund für manuelle Preisänderung (Pflicht bei Abweichung)")} hint={t("Wird im Protokoll festgehalten (von → zu + Grund).")}>
                  <input data-testid="cc-override-reason" className={inputCls} value={f.price_override_reason} onChange={(e) => set("price_override_reason", e.target.value)} placeholder="z. B. Rabatt Altkunde, Sonderkondition, Aktion" />
                </Field>
              </div>
            )}
          </Card>

          {/* Inklusive Module */}
          <Card id="cc-sec-module" className="p-7 space-y-5">
            <CardHead icon={Lock}>{t("Im Paket inklusive")}</CardHead>
            {includedObjs.length > 0 ? (
              <>
                <div className="flex flex-wrap gap-2.5">
                  {includedObjs.map((m) => (
                    <div key={m.id} data-testid={`cc-incl-${m.key}`} className="inline-flex items-center gap-2 pl-2.5 pr-3.5 py-2 rounded-full border border-[#A3E635]/30 bg-[#A3E635]/5">
                      <div className="w-4 h-4 rounded-full bg-[#A3E635] flex items-center justify-center shrink-0"><Check className="w-3 h-3 text-black" /></div>
                      <span className="text-[13px] font-medium text-gray-200">{m.key === "report" && tier === "start" ? t("Monatsreport Light") : (m.display_name || m.name)}</span>
                    </div>
                  ))}
                </div>
                {tier && TIER_BENEFITS[tier] && (
                  <div className="rounded-xl border border-[#A3E635]/40 bg-gradient-to-r from-[#A3E635]/[0.10] to-[#A3E635]/[0.03] p-4" data-testid="cc-tier-benefits">
                    <div className="text-[12px] font-black uppercase tracking-wider text-[#A3E635] mb-2.5 flex items-center gap-2"><Sparkles className="w-4 h-4" /> {t("Zusätzlich im {pkg} enthalten").replace("{pkg}", (selectedPkg?.display_name || selectedPkg?.name)?.toUpperCase() || "")}</div>
                    <div className="grid sm:grid-cols-2 gap-2">
                      {TIER_BENEFITS[tier].map((b) => <div key={b} className="text-[13px] text-gray-200 flex items-start gap-2"><Check className="w-4 h-4 text-[#A3E635] shrink-0 mt-0.5" /> {b}</div>)}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <p className="text-[14px] text-gray-400">{t("INDIVIDUELL: Module frei wählbar — als Offerte vorbereiten.")}</p>
            )}
            {groupBASIS.length > 0 && (
              <div data-testid="cc-group-BASIS">
                <div className="text-[12px] font-bold text-gray-200 uppercase tracking-wider mt-2 mb-2.5">{t("Basis-Module (frei wählen)")}</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">{groupBASIS.map((m) => renderMod(m, "BASIS"))}</div>
              </div>
            )}
          </Card>

          {/* Zusatzmodule */}
          {groupA.length > 0 && (
            <Card className="p-7 space-y-6" data-testid="cc-group-A">
              <CardHead hint={t("Zusätzlicher Umsatz — sauber getrennt nach monatlich und einmalig.")}>{t("Zusatzmodule — separat verkaufbar")}</CardHead>
              {groupAMonthly.length > 0 && (
                <div data-testid="cc-group-A-monthly">
                  <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-[#A3E635] mb-3"><Repeat className="w-4 h-4" /> {t("Monatlich verrechnet")}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">{groupAMonthly.map((m) => renderMod(m, "A"))}</div>
                </div>
              )}
              {groupAOnce.length > 0 && (
                <div data-testid="cc-group-A-once" className="pt-1">
                  <div className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.16em] text-gray-300 mb-3"><Coins className="w-4 h-4" /> {t("Einmalig verrechnet")}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">{groupAOnce.map((m) => renderMod(m, "A"))}</div>
                </div>
              )}
            </Card>
          )}

          {/* Empfehlung + Individuell offerieren */}
          {(groupB.length > 0 || groupC.length > 0) && (
            <div className="grid lg:grid-cols-2 gap-6">
              {groupB.length > 0 && (
                <Card className="p-7" data-testid="cc-group-B">
                  <CardHead hint={t("Eigenes Angebot — kein stiller CHF-0-Eintrag.")}>{t("Empfehlung vorbereiten")}</CardHead>
                  <div className="grid gap-3">{groupB.map((m) => renderMod(m, "B"))}</div>
                </Card>
              )}
              {groupC.length > 0 && (
                <Card className="p-7 border-[#A3E635]/30" data-testid="cc-group-C">
                  <CardHead hint={t("Immer separate Offerte — Preis nach Aufwand.")}>{t("Individuell offerieren")}</CardHead>
                  <div className="grid gap-3">{groupC.map((m) => renderMod(m, "C"))}</div>
                </Card>
              )}
            </div>
          )}

          {/* Paketvergleich → als vollbreites Board unter dem Grid */}
        </div>

        {/* ===== RECHTS (sticky): Preis, Prüfliste, Notizen, Aktionen ===== */}
        <div id="cc-sec-summary">
          <div className="xl:sticky xl:top-6 space-y-5">
            <PricePanel />

            <Card className="p-5" data-testid="cc-checklist">
              <div className="text-[13px] font-bold uppercase tracking-wider text-gray-300 mb-3 flex items-center gap-2"><ListChecks className="w-4 h-4 text-[#A3E635]" /> {t("Validierungs-Prüfliste")}</div>
              <div className="space-y-2">
                {checks.map((c, i) => (
                  <div key={i} className="flex items-center gap-2.5 text-[13px]" data-testid={`cc-check-${i}`}>
                    {c.ok ? <CheckCircle2 className="w-4 h-4 text-[#A3E635] shrink-0" /> : <Circle className="w-4 h-4 text-gray-600 shrink-0" />}
                    <span className={c.ok ? "text-gray-200" : "text-gray-500"}>{c.label}</span>
                  </div>
                ))}
              </div>
            </Card>

            <Card className="p-5" data-testid="cc-notes-card">
              <div className="text-[13px] font-bold uppercase tracking-wider text-gray-300 mb-3 flex items-center gap-2"><FileText className="w-4 h-4 text-[#A3E635]" /> {t("Interne Notizen")}</div>
              <textarea data-testid="cc-notes" className={inputCls} rows={4} value={f.internal_notes} onChange={(e) => set("internal_notes", e.target.value)} placeholder={t("Interne Notizen...")} />
              <label className="flex items-center gap-2.5 text-[14px] cursor-pointer mt-3">
                <input type="checkbox" data-testid="cc-prepare-invoice" checked={f.prepare_invoice} onChange={(e) => set("prepare_invoice", e.target.checked)} className="w-5 h-5 accent-[#A3E635]" />
                <span className="text-gray-300">{t("bexio-Rechnung vorbereiten (Setup + 1. Monat)")}</span>
              </label>
            </Card>

            {attempted && errors.length > 0 && (
              <Card className="p-4 space-y-2 border-red-400/30" data-testid="cc-warnings">
                {errors.map((er, i) => <div key={`e${i}`} className="text-[13px] text-red-300 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {t(er)}</div>)}
              </Card>
            )}
            {warnings.length > 0 && (
              <Card className="p-4 space-y-2 border-amber-400/30" data-testid="cc-soft-warnings">
                {warnings.map((wn, i) => <div key={`w${i}`} className="text-[13px] text-amber-300 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {t(wn)}</div>)}
              </Card>
            )}

            <Card className="p-5" data-testid="cc-actions">
              <div className="text-[13px] font-bold uppercase tracking-wider text-gray-300 mb-3 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#A3E635]" /> {t("Aktionen")}</div>
              <button onClick={goReview} data-testid="cc-to-review"
                className="w-full bg-[#A3E635] hover:bg-[#BEF264] text-black font-bold py-3.5 rounded-xl transition-colors flex items-center justify-center gap-2 text-[15px] shadow-[0_0_24px_rgba(163,230,53,0.18)]">
                <ListChecks className="w-5 h-5" /> {t("Prüfen & erstellen")}
              </button>
              <div className="grid grid-cols-2 gap-2.5 mt-2.5">
                <button onClick={validateOnly} data-testid="cc-validate"
                  className="bg-white/5 hover:bg-white/10 border border-white/10 py-3 rounded-xl text-[14px] font-medium flex items-center justify-center gap-2">
                  <ShieldCheck className="w-4 h-4" /> {t("Prüfen")}
                </button>
                <button onClick={saveDraft} disabled={savingDraft} data-testid="cc-save-draft"
                  className="bg-white/5 hover:bg-white/10 border border-white/10 py-3 rounded-xl text-[14px] font-medium flex items-center justify-center gap-2 disabled:opacity-60">
                  <Save className="w-4 h-4" /> {savingDraft ? t("Speichert…") : t("Entwurf speichern")}
                </button>
              </div>
              <button onClick={() => nav("/admin/clients")} data-testid="cc-cancel"
                className="w-full bg-transparent hover:bg-white/5 border border-white/10 py-3 rounded-xl text-[14px] text-gray-400 mt-2.5">{t("Abbrechen")}</button>
              {attempted && errors.length > 0 && <p className="text-[12px] text-gray-500 text-center mt-3">{t("Bitte zuerst alle roten Punkte beheben.")}</p>}
            </Card>
          </div>
        </div>
      </div>

      {/* === Premium Paketvergleich (volle Breite) — Entscheidungs-Board === */}
      <div className="mt-10" data-testid="cc-comparison">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.18em] text-[#A3E635] mb-2.5"><ListChecks className="w-4 h-4" /> {t("Entscheidungshilfe")}</div>
            <h3 className="text-[26px] sm:text-[30px] leading-9 font-black text-white tracking-tight">{t("Paketvergleich auf einen Blick")}</h3>
            <p className="text-[14px] text-gray-400 mt-2 max-w-2xl">{t("Leistungen im direkten Vergleich. WACHSTUM empfehlen wir für nachhaltiges Wachstum — „Auswählen“ übernimmt das Paket direkt in den Assistenten.")}</p>
          </div>
          <div className="inline-flex items-center gap-5 text-[12px] text-gray-400">
            <span className="inline-flex items-center gap-1.5"><span className="w-5 h-5 rounded-full bg-[#A3E635]/15 flex items-center justify-center"><Check className="w-3 h-3 text-[#A3E635]" /></span> {t("enthalten")}</span>
            <span className="inline-flex items-center gap-1.5 text-gray-600"><span className="text-base">—</span> {t("nicht enthalten")}</span>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_340px] gap-6 items-start">
          {/* Vergleichstabelle */}
          <Card className="p-5 sm:p-6 lg:p-7">
            <div className="overflow-x-auto -mx-2 px-2 pt-2 pb-1">
              <table className="w-full border-collapse min-w-[700px]">
                <thead>
                  <tr>
                    <th className="text-left align-bottom p-3 w-[28%]"><span className="text-[12px] font-bold uppercase tracking-wider text-gray-500">{t("Leistung")}</span></th>
                    {cmpPkgs.map((p, i) => {
                      const rec = i === recIdx;
                      return (
                        <th key={p.id} className={`p-3 align-top text-center ${rec ? "bg-[#A3E635]/[0.08] border-x-2 border-t-2 border-[#A3E635]/50 rounded-t-2xl shadow-[0_-1px_28px_rgba(163,230,53,0.10)]" : ""}`}>
                          <div className="flex flex-col items-center gap-1.5">
                            {rec ? (
                              <span className="inline-flex items-center gap-1 text-[10px] font-black px-2.5 py-1 rounded-full bg-[#A3E635] text-black uppercase tracking-wider shadow-[0_4px_14px_rgba(163,230,53,0.4)]"><Crown className="w-3 h-3" /> {t("Empfohlen")}</span>
                            ) : <span className="h-[22px]" />}
                            <div className={`text-[16px] font-black tracking-tight ${rec ? "text-[#A3E635]" : "text-white"}`}>{(p.display_name || p.name).toUpperCase()}</div>
                            {p.custom ? (
                              <>
                                <div className="text-[15px] font-bold text-white mt-0.5">{t("Auf Anfrage")}</div>
                                <div className="text-[11px] text-[#A3E635]">{t("Offerte vorbereiten")}</div>
                              </>
                            ) : (
                              <>
                                <div className="text-[21px] font-black tracking-tighter mt-0.5 text-white">CHF {p.price_chf}.–<span className="text-[11px] text-gray-500 font-normal">/Mt.</span></div>
                                <div className="text-[11px] text-gray-500">Setup CHF {p.setup_fee}.–</div>
                                <div className="text-[11px] text-gray-400">24 Mt.: <span className="font-semibold text-gray-200">{chf(p.setup_fee + p.price_chf * 24)}</span></div>
                              </>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {CMP_GROUPS.map((g) => (
                    <React.Fragment key={g.title}>
                      <tr>
                        <td colSpan={1 + cmpPkgs.length} className="pt-6 pb-2 px-3">
                          <div className="flex items-center gap-2 text-[12px] font-black uppercase tracking-[0.16em] text-[#A3E635]">
                            <span className="w-7 h-7 rounded-lg bg-[#A3E635]/12 border border-[#A3E635]/20 flex items-center justify-center"><g.icon className="w-4 h-4" /></span> {t(g.title)}
                          </div>
                        </td>
                      </tr>
                      {g.rows.map(([label, vals]) => (
                        <tr key={label} className="border-t border-white/[0.06]">
                          <td className="py-2.5 px-3 text-[14px] text-gray-300">{t(label)}</td>
                          {vals.map((v, i) => {
                            const rec = i === recIdx;
                            return (
                              <td key={i} className={`py-2.5 px-3 text-center text-[13px] ${rec ? "bg-[#A3E635]/[0.06] text-white font-semibold border-x-2 border-[#A3E635]/50" : "text-gray-300"}`}>
                                {v === "Ja" ? <span className="inline-flex w-6 h-6 rounded-full bg-[#A3E635]/15 items-center justify-center"><Check className="w-3.5 h-3.5 text-[#A3E635]" /></span> : (v === "–" || v === "Nein") ? <span className="text-gray-600 text-lg">—</span> : v}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                  <tr className="border-t border-white/10">
                    <td className="p-3"></td>
                    {cmpPkgs.map((p, i) => {
                      const rec = i === recIdx; const sel = f.package_id === p.id;
                      return (
                        <td key={p.id} className={`p-3 text-center ${rec ? "bg-[#A3E635]/[0.06] border-x-2 border-b-2 border-[#A3E635]/50 rounded-b-2xl shadow-[0_1px_28px_rgba(163,230,53,0.10)]" : ""}`}>
                          <button type="button" data-testid={`cc-cmp-select-${p.name}`} onClick={() => { onPackage(p); goTo("cc-sec-paket"); }}
                            className={`w-full font-bold py-2.5 px-3 rounded-lg text-[13px] transition-colors ${sel ? "bg-[#A3E635] text-black" : rec ? "bg-[#A3E635] text-black hover:bg-[#BEF264]" : "bg-white/5 text-gray-200 hover:bg-white/10 border border-white/10"}`}>
                            {sel ? t("Gewählt") : t("Auswählen")}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          {/* Premium Info-Karte */}
          <Card className="p-6 lg:p-7 xl:sticky xl:top-6 bg-gradient-to-b from-[#A3E635]/[0.06] to-transparent border-[#A3E635]/20" data-testid="cc-kewag-extras">
            <div className="flex items-center gap-2 mb-1.5">
              <Sparkles className="w-5 h-5 text-[#A3E635] shrink-0" />
              <h4 className="text-[18px] font-black text-white tracking-tight leading-6">{t("Was bei KEWAGPage zusätzlich drin ist")}</h4>
            </div>
            <p className="text-[13px] text-gray-400 mb-5">{t("Der Unterschied zum reinen Website-Baukasten.")}</p>
            <div className="space-y-0.5">
              {KEWAG_EXTRAS.map((x, i) => (
                <div key={i} className={`flex items-start gap-3 py-3 ${i < KEWAG_EXTRAS.length - 1 ? "border-b border-white/[0.06]" : ""}`} data-testid={`cc-extra-${i}`}>
                  <span className="w-9 h-9 rounded-xl bg-[#A3E635]/12 border border-[#A3E635]/20 flex items-center justify-center shrink-0"><x.icon className="w-4 h-4 text-[#A3E635]" /></span>
                  <div className="min-w-0">
                    <div className="text-[14px] font-semibold text-gray-100 leading-snug">{t(x.title)}</div>
                    <div className="text-[12px] text-gray-500 mt-0.5 leading-snug">{t(x.desc)}</div>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v, bold, accent }) => (
  <div className="flex items-center justify-between">
    <span className={`text-gray-400 ${accent ? "text-[#A3E635]" : ""}`}>{k}</span>
    <span className={`${bold ? "font-bold text-white" : accent ? "text-[#A3E635] font-medium" : "text-gray-200"}`}>{v}</span>
  </div>
);
